import { Fragment, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { cn } from '@/lib/utils'

/**
 * The assistant writes a small Markdown subset: paragraphs, **bold**,
 * numbered and bulleted lists, and inline links. App-relative links
 * (`/dashboard/...`) navigate in-app; anything else opens in a new tab.
 */
type Block =
  | { type: 'p'; lines: string[] }
  | { type: 'ol'; start: number; items: string[] }
  | { type: 'ul'; items: string[] }
  | { type: 'h'; text: string }

const OL = /^\s*(\d+)[.)]\s+(.*)$/
const UL = /^\s*[-*•]\s+(.*)$/
const H = /^\s*#{1,6}\s+(.*)$/

export function parseBlocks(source: string): Block[] {
  const blocks: Block[] = []
  let paragraph: string[] = []
  const flush = () => {
    if (paragraph.length) blocks.push({ type: 'p', lines: paragraph })
    paragraph = []
  }
  for (const raw of source.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trimEnd()
    if (!line.trim()) {
      flush()
      continue
    }
    const ol = OL.exec(line)
    if (ol) {
      flush()
      const last = blocks[blocks.length - 1]
      if (last?.type === 'ol') last.items.push(ol[2] ?? '')
      else blocks.push({ type: 'ol', start: Number(ol[1]) || 1, items: [ol[2] ?? ''] })
      continue
    }
    const ul = UL.exec(line)
    if (ul) {
      flush()
      const last = blocks[blocks.length - 1]
      if (last?.type === 'ul') last.items.push(ul[1] ?? '')
      else blocks.push({ type: 'ul', items: [ul[1] ?? ''] })
      continue
    }
    const h = H.exec(line)
    if (h) {
      flush()
      blocks.push({ type: 'h', text: h[1] ?? '' })
      continue
    }
    paragraph.push(line.trim())
  }
  flush()
  return blocks
}

const INLINE = /(\*\*[^*\n]+?\*\*|\[[^\]\n]+\]\([^)\s]+\)|`[^`\n]+`)/g
const LINK = /^\[([^\]]+)\]\(([^)\s]+)\)$/

export function renderInline(text: string, onNavigate?: () => void): ReactNode {
  const parts = text.split(INLINE).filter((part) => part !== '')
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong key={index} className="font-semibold text-ink">
          {renderInline(part.slice(2, -2), onNavigate)}
        </strong>
      )
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code key={index} className="rounded-[5px] bg-surface-muted px-1 py-0.5 font-mono text-[0.9em] text-ink-soft">
          {part.slice(1, -1)}
        </code>
      )
    }
    const link = LINK.exec(part)
    if (link) {
      const [, label = '', href = ''] = link
      const className = 'font-medium text-brand underline decoration-brand/40 underline-offset-2 hover:decoration-brand'
      if (href.startsWith('/')) {
        return (
          <Link key={index} to={href} onClick={onNavigate} className={className}>
            {renderInline(label, onNavigate)}
          </Link>
        )
      }
      return (
        <a key={index} href={href} target="_blank" rel="noreferrer" className={className}>
          {renderInline(label, onNavigate)}
        </a>
      )
    }
    return <Fragment key={index}>{part}</Fragment>
  })
}

export function Markdown({ content, className, onNavigate }: { content: string; className?: string; onNavigate?: () => void }) {
  const blocks = parseBlocks(content)
  return (
    <div className={cn('space-y-2.5 break-words text-[14.5px] leading-relaxed text-ink', className)}>
      {blocks.map((block, index) => {
        if (block.type === 'h') {
          return (
            <p key={index} className="font-semibold text-ink">
              {renderInline(block.text, onNavigate)}
            </p>
          )
        }
        if (block.type === 'ol') {
          return (
            <ol key={index} start={block.start} className="list-decimal space-y-1.5 pl-5 marker:font-medium marker:text-ink-muted">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex} className="pl-0.5">{renderInline(item, onNavigate)}</li>
              ))}
            </ol>
          )
        }
        if (block.type === 'ul') {
          return (
            <ul key={index} className="list-disc space-y-1.5 pl-5 marker:text-ink-faint">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex} className="pl-0.5">{renderInline(item, onNavigate)}</li>
              ))}
            </ul>
          )
        }
        return (
          <p key={index}>
            {block.lines.map((line, lineIndex) => (
              <Fragment key={lineIndex}>
                {lineIndex > 0 && <br />}
                {renderInline(line, onNavigate)}
              </Fragment>
            ))}
          </p>
        )
      })}
    </div>
  )
}
